import React, { useState, useEffect, useRef } from 'react';
import './firebaseConfig.js';
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, doc, onSnapshot, setDoc, collection, getDocs, deleteDoc } from "firebase/firestore";
import SplashScreen from './components/SplashScreen';
import SetupScreen from './components/SetupScreen';
import GameScreen from './components/GameScreen';
import HistoryScreen from './components/HistoryScreen';
import StatisticsScreen from './components/StatisticsScreen';
import Login from './components/Login';
import SignUp from './components/SignUp';
import LoadGameScreen from './components/LoadGameScreen';
import PlayerPerformanceScreen from './components/PlayerPerformanceScreen';
import ArchivedGameScreen from './components/ArchivedGameScreen';
import PlayerManagementScreen from './components/PlayerManagementScreen';
import UserInfo from './components/UserInfo';
import { getPlayerColor } from './utils';

const App = () => {
    const [screen, setScreen] = useState('login'); // login, signup, splash, setup, playing, finished, history, statistics, loadGame, playerPerformance, archivedGame, playerManagement
    const [gameState, setGameState] = useState(null); // playing, finished, setup
    const [user, setUser] = useState(null);
    const [players, setPlayers] = useState([]);
    const [numDecks, setNumDecks] = useState(1);
    const [tricksPerHand, setTricksPerHand] = useState(13);
    const [currentHand, setCurrentHand] = useState(1);
    const [handHistory, setHandHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [gameId, setGameId] = useState(null);
    const [isGameInProgress, setIsGameInProgress] = useState(false);

    const [viewingArchivedGame, setViewingArchivedGame] = useState(null);
    const [savedPlayers, setSavedPlayers] = useState([]);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const appId = "1:699575750232:web:8d7ebbfd7a7bb5c866842d";
    const completedGameIdRef = useRef(null);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const OfflineBanner = () => (
        !isOnline && (
            <div className="fixed bottom-0 left-0 right-0 bg-red-500 text-white text-center text-xs font-bold py-1 z-[100]">
                You are offline. Changes will sync when connection returns.
            </div>
        )
    );

    useEffect(() => {
        const savedScreen = localStorage.getItem('screen');
        if (savedScreen) {
            setScreen(savedScreen);
        }
        const savedViewingArchivedGame = localStorage.getItem('viewingArchivedGame');
        if (savedViewingArchivedGame) {
            setViewingArchivedGame(JSON.parse(savedViewingArchivedGame));
        }
    }, []);

    useEffect(() => {
        if (!isLoading) {
            localStorage.setItem('screen', screen);
        }
    }, [screen, isLoading]);

    useEffect(() => {
        if (viewingArchivedGame) {
            localStorage.setItem('viewingArchivedGame', JSON.stringify(viewingArchivedGame));
        } else {
            localStorage.removeItem('viewingArchivedGame');
        }
    }, [viewingArchivedGame]);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
                const savedScreen = localStorage.getItem('screen');
                if (savedScreen && savedScreen !== 'login' && savedScreen !== 'signup') {
                    setScreen(savedScreen);
                } else {
                    setScreen('splash');
                }
            } else {
                setUser(null);
                setScreen('login');
                setPlayers([]);
                setHandHistory([]);
                setSavedPlayers([]);
                setGameState(null);
                setIsGameInProgress(false);
                setMostRecentPausedGame(null);
            }
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, []);


    useEffect(() => {
        if (user) {
            const db = getFirestore();
            const docRef = doc(db, "personalData", appId, "users", user.uid, "game", "state");
            const unsubscribe = onSnapshot(docRef, (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    let newGameState = data.gameState || null;
                    // Prevent reverting to 'playing' if we know it's finished locally
                    if (completedGameIdRef.current === data.gameId && newGameState === 'playing') {
                        newGameState = 'finished';
                    }
                    setGameState(newGameState);
                    setPlayers(data.players || []);
                    setNumDecks(data.numDecks || 1);
                    setTricksPerHand(data.tricksPerHand || 13);
                    setCurrentHand(data.currentHand || 1);
                    setHandHistory(data.handHistory || []);
                    setGameId(data.gameId || null);
                    if (data.gameState === 'playing') {
                        setIsGameInProgress(true);
                    } else {
                        setIsGameInProgress(false);
                    }
                }
            });
            return () => unsubscribe();
        }
    }, [user]);

    // Load saved players from Firestore
    useEffect(() => {
        if (user) {
            const db = getFirestore();
            const docRef = doc(db, "personalData", appId, "users", user.uid, "settings", "players");
            const unsubscribe = onSnapshot(docRef, (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    setSavedPlayers(data.playerList || []);
                } else {
                    setSavedPlayers([]);
                }
            });
            return () => unsubscribe();
        }
    }, [user]);

    const [mostRecentPausedGame, setMostRecentPausedGame] = useState(null);

    // Check for most recent paused game
    useEffect(() => {
        if (user) {
            const db = getFirestore();
            const fetchMostRecentPausedGame = async () => {
                const gamesRef = collection(db, "personalData", appId, "users", user.uid, "games");

                try {
                    const querySnapshot = await getDocs(gamesRef);
                    const games = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    // Sort by date descending
                    games.sort((a, b) => new Date(b.date) - new Date(a.date));

                    const recentPaused = games.find(game => game.handHistory && game.handHistory.length < 13);
                    setMostRecentPausedGame(recentPaused || null);
                } catch (error) {
                    console.error("Error fetching games:", error);
                }
            };

            fetchMostRecentPausedGame();
        }
    }, [user, screen]); // Re-fetch when screen changes (e.g. coming back from game)
    useEffect(() => {
        if (user && !isLoading && (gameState === 'playing' || gameState === 'setup' || gameState === 'finished')) {
            const db = getFirestore();
            const docRef = doc(db, "personalData", appId, "users", user.uid, "game", "state");
            const stateToSave = {
                gameState,
                players,
                numDecks,
                tricksPerHand,
                currentHand,
                handHistory,
                gameId
            };
            setDoc(docRef, stateToSave);

            // Also save in-progress games to the games collection so they appear in Load Game
            if (gameState === 'playing' && gameId && handHistory.length > 0 && handHistory.length < 13) {
                const inProgressGame = {
                    date: new Date().toISOString(),
                    players: players,
                    handHistory: handHistory,
                    gameState: 'playing'
                };
                const gameDocRef = doc(db, "personalData", appId, "users", user.uid, "games", gameId);
                setDoc(gameDocRef, inProgressGame);
            }
        }
    }, [gameState, players, numDecks, tricksPerHand, currentHand, handHistory, user, isLoading, gameId, appId]);


    useEffect(() => {
        // Only trigger game completion if we're actively playing and just finished the 13th hand
        // Don't trigger if we're loading a finished game from Firestore
        if (user && handHistory.length === 13 && gameState === 'playing' && gameId && screen === 'playing') {
            if (completedGameIdRef.current === gameId) return;
            completedGameIdRef.current = gameId;

            setGameState('finished');
            setScreen('finished');

            const maxScore = Math.max(...players.map(p => p.score));
            const winners = players.filter(p => p.score === maxScore);
            // Keep single winner for backward compatibility if needed, but rely on winners array
            const winner = winners.length === 1 ? winners[0] : winners[0];

            const completedGame = {
                date: new Date().toISOString(),
                players: players,
                handHistory: handHistory,
                winner, // Legacy support
                winners // New support for ties
            };

            const db = getFirestore();
            const docRef = doc(db, "personalData", appId, "users", user.uid, "games", gameId);
            setDoc(docRef, completedGame);
        }
    }, [handHistory, players, gameState, user, appId, gameId, screen]);

    const handleNewGame = () => {
        setScreen('setup');
        setGameState('setup');
    };

    const handleShowHistory = () => {
        setScreen('history');
    };

    const handleShowStats = () => {
        setScreen('statistics');
    };

    const handleLoadGame = () => {
        setScreen('loadGame');
    };

    const handleShowPlayerPerformance = () => {
        setScreen('playerPerformance');
    };

    const handleManagePlayers = () => {
        setScreen('playerManagement');
    };

    const handleUpdatePlayers = (updatedPlayers) => {
        setSavedPlayers(updatedPlayers);
        if (user) {
            const db = getFirestore();
            const docRef = doc(db, "personalData", appId, "users", user.uid, "settings", "players");
            setDoc(docRef, { playerList: updatedPlayers });
        }
    };





    const handleStartGame = (playerNames) => {
        const numPlayers = playerNames.length;
        setGameId(Date.now().toString());
        completedGameIdRef.current = null;

        // Calculate numDecks - this is just for display
        let decks;
        if (numPlayers === 4) decks = 1;
        else if (numPlayers >= 5 && numPlayers <= 7) decks = 2;
        else decks = 3;

        setNumDecks(decks);
        setTricksPerHand(13); // This is just stored for reference, actual tricks = currentHand

        // Initialize players
        const initialPlayers = playerNames.map((name, i) => ({
            name,
            color: getPlayerColor(i),
            score: 0,
            bags: 0
        }));

        setPlayers(initialPlayers);
        setCurrentHand(1);
        setHandHistory([]);
        setGameState('playing');
        setScreen('playing');
    };

    const handlePlayAgain = () => {
        setPlayers([]);
        setHandHistory([]);
        setCurrentHand(1);
        setScreen('setup');
        setGameState('setup');
    };

    const handleGoToSignUp = () => {
        setScreen('signup');
    };

    const handleGoToLogin = () => {
        setScreen('login');
    };

    const handleGoHome = () => {
        setScreen('splash');
    };

    const handleLogout = () => {
        const auth = getAuth();
        if (user) {
            const db = getFirestore();
            const docRef = doc(db, "personalData", appId, "users", user.uid, "game", "state");
            setDoc(docRef, { gameState: 'finished' });
        }
        signOut(auth);
        localStorage.removeItem('screen');
        localStorage.removeItem('viewingArchivedGame');
        localStorage.removeItem('lastGamePlayers');
    };

    const handlePauseGame = () => {
        if (user && gameId) {
            const db = getFirestore();

            // Save current game state to games collection so it can be resumed
            const inProgressGame = {
                date: new Date().toISOString(),
                players: players,
                handHistory: handHistory,
                gameState: 'playing',
                tricksPerHand,
                numDecks,
                currentHand
            };
            const gameDocRef = doc(db, "personalData", appId, "users", user.uid, "games", gameId);
            setDoc(gameDocRef, inProgressGame);

            // Clear active game state
            const stateDocRef = doc(db, "personalData", appId, "users", user.uid, "game", "state");
            setDoc(stateDocRef, { gameState: 'finished' });
        }
        setScreen('splash');
    };

    const handleQuitGame = async () => {
        if (user && gameId) {
            const db = getFirestore();

            // Delete from games collection if it exists (to prevent resuming)
            const gameDocRef = doc(db, "personalData", appId, "users", user.uid, "games", gameId);
            await deleteDoc(gameDocRef);

            // Clear active game state
            const stateDocRef = doc(db, "personalData", appId, "users", user.uid, "game", "state");
            setDoc(stateDocRef, { gameState: 'finished' });
        }
        setScreen('splash');
    };


    const gameConfig = {
        tricksPerHand,
    };

    const handleResumePausedGame = (game) => {
        setPlayers(game.players);
        setHandHistory(game.handHistory);
        setCurrentHand(game.currentHand || game.handHistory.length + 1);
        setGameId(game.id);
        setNumDecks(game.numDecks || 1);
        setTricksPerHand(game.tricksPerHand || 13);
        setGameState('playing');
        setScreen('playing');
    };


    const handleResumeGame = () => {
        if (mostRecentPausedGame) {
            handleResumePausedGame(mostRecentPausedGame);
        } else {
            setScreen('playing');
        }
    };

    const renderScreen = () => {
        if (isLoading) {
            return <div>Loading...</div>;
        }

        if (!user) {
            if (screen === 'login') {
                return <Login onSignIn={() => setScreen('splash')} onGoToSignUp={handleGoToSignUp} />;
            }
            if (screen === 'signup') {
                return <SignUp onSignIn={() => setScreen('splash')} onGoToLogin={handleGoToLogin} />;
            }
            return null;
        }

        if (screen === 'splash') {
            return <SplashScreen
                onNewGame={handleNewGame}
                onShowHistory={handleShowHistory}
                onLoadGame={handleLoadGame}
                onShowPlayerPerformance={handleShowPlayerPerformance}
                onManagePlayers={handleManagePlayers}
                isGameInProgress={isGameInProgress || !!mostRecentPausedGame}
                onResumeGame={handleResumeGame}
                onLogout={handleLogout}
                mostRecentPausedGame={mostRecentPausedGame}
            />;
        }

        if (screen === 'setup') {
            return <SetupScreen user={user} onStartGame={handleStartGame} savedPlayers={savedPlayers} onBack={() => setScreen('splash')} onHome={handleGoHome} onLogout={handleLogout} />;
        }

        if (screen === 'history') {
            return <HistoryScreen onBack={() => setScreen('splash')} onHome={handleGoHome} onLogout={handleLogout} />;
        }

        if (screen === 'loadGame') {
            return <LoadGameScreen onBack={() => setScreen('splash')} onSelectGame={handleResumePausedGame} onHome={handleGoHome} onLogout={handleLogout} />;
        }

        if (screen === 'playerPerformance') {
            return <PlayerPerformanceScreen onBack={() => setScreen('splash')} onHome={handleGoHome} onLogout={handleLogout} />;
        }

        if (screen === 'playerManagement') {
            return <PlayerManagementScreen savedPlayers={savedPlayers} onUpdatePlayers={handleUpdatePlayers} onBack={() => setScreen('splash')} onHome={handleGoHome} onLogout={handleLogout} />;
        }

        if (screen === 'statistics') {
            const statsPlayers = viewingArchivedGame ? viewingArchivedGame.players : players;
            const statsHandHistory = viewingArchivedGame ? viewingArchivedGame.handHistory : handHistory;
            return <StatisticsScreen
                initialPlayers={statsPlayers}
                initialHandHistory={statsHandHistory}
                gameConfig={gameConfig}
                gameState={viewingArchivedGame ? 'finished' : gameState}
                onClose={() => {
                    if (viewingArchivedGame) {
                        setScreen('archivedGame');
                    } else {
                        setScreen(gameState);
                    }
                }}
            />;
        }

        if (screen === 'archivedGame' && viewingArchivedGame) {
            return <ArchivedGameScreen
                players={viewingArchivedGame.players}
                handHistory={viewingArchivedGame.handHistory}
                gameState={'finished'}
                gameConfig={gameConfig}
                onBack={() => {
                    setViewingArchivedGame(null);
                    setScreen('loadGame');
                }}
            />;
        }

        if (screen === 'playing' || screen === 'finished') {
            return <GameScreen
                players={players}
                setPlayers={setPlayers}
                numDecks={numDecks}
                tricksPerHand={tricksPerHand}
                currentHand={currentHand}
                setCurrentHand={setCurrentHand}
                handHistory={handHistory}
                setHandHistory={setHandHistory}
                gameState={gameState}
                setGameState={setGameState}
                onNewGame={handlePlayAgain}
                onShowStats={handleShowStats}
                gameConfig={gameConfig}
                onHome={handleGoHome}
                onLogout={handleLogout}
                onPauseGame={handlePauseGame}
                onQuitGame={handleQuitGame}
            />;
        }

        return null;
    };

    return (
        <>
            <OfflineBanner />
            <UserInfo user={user} screen={screen} />
            {renderScreen()}
        </>
    );
};

export default App;